import { useSeoMeta } from "@unhead/react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Award,
  BookMarked,
  BrainCircuit,
  Flame,
  GraduationCap,
  Sparkles,
  Zap,
} from "lucide-react";
import { Layout } from "@/components/Layout";
import { ModuleCard } from "@/components/ModuleCard";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { MODULES } from "@/lib/curriculum";
import { useProgress } from "@/hooks/useProgress";
import { NextSteps } from "@/components/NextSteps";
import { ResetProgressButton } from "@/components/ResetProgressButton";
import { ShareSiteButton } from "@/components/ShareSiteButton";

const Index = () => {
  useSeoMeta({
    title: "BCH School — Learn Bitcoin Cash by doing",
    description:
      "A fun, interactive, beginner-friendly way to learn Bitcoin Cash. Earn XP, collect badges, and get your completion certificate. All examples are simulated — no real money.",
    ogImage: "https://bchschool.com/og-image.jpg",
  });

  const {
    xp,
    totalXp,
    percent,
    completedCount,
    totalLessons,
    moduleCompletion,
    earnedBadges,
    nextLesson,
    allLessonsComplete,
    quizPassed,
    currentStreak,
    activeToday,
  } = useProgress();

  const started = completedCount > 0;

  // Where the primary hero button should take the learner: their next
  // unfinished lesson, or the quiz / certificate once lessons are done.
  const continueTo = nextLesson
    ? `/lesson/${nextLesson.id}`
    : quizPassed
      ? "/certificate"
      : "/quiz";

  const continueLabel = !started
    ? "Start learning"
    : nextLesson
      ? "Continue learning"
      : quizPassed
        ? "View your certificate"
        : "Take the final quiz";

  return (
    <Layout>
      {/* Hero */}
      <section className="mb-10 overflow-hidden rounded-3xl border-2 bg-card shadow-sm">
        <div className="grid items-center gap-6 p-6 sm:p-10 md:grid-cols-2">
          <div className="space-y-5">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">
              <Sparkles className="h-3.5 w-3.5" /> Beginner friendly · 100% simulated
            </span>
            <h1 className="text-4xl font-extrabold leading-tight tracking-tight sm:text-5xl">
              Learn Bitcoin Cash{" "}
              <span className="bg-gradient-to-r from-green-500 to-emerald-600 bg-clip-text text-transparent">
                by doing.
              </span>
            </h1>
            <p className="text-lg text-muted-foreground">
              Your money quietly loses value to inflation every year. Learn how
              sound, peer-to-peer digital cash fixes that — through short,
              interactive lessons where you decide, see what happens, and
              understand why. Earn XP, collect badges, and graduate.
            </p>
            <div className="flex flex-wrap gap-3">
              <Button asChild size="lg" className="text-base">
                <Link to={continueTo}>
                  {continueLabel}
                  <ArrowRight className="ml-1 h-5 w-5" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="text-base">
                <Link to="/badges">
                  <Award className="mr-1 h-5 w-5" /> Badges
                </Link>
              </Button>
              <Button asChild size="lg" variant="ghost" className="text-base">
                <Link to="/glossary">
                  <BookMarked className="mr-1 h-5 w-5" /> Glossary
                </Link>
              </Button>
            </div>
            {!started && (
              <Link
                to="/module/inflation"
                className="group inline-flex items-center gap-2 rounded-full border border-amber-500/30 bg-amber-500/10 px-4 py-2 text-sm font-semibold text-amber-700 transition-colors hover:bg-amber-500/20 dark:text-amber-400"
              >
                <span aria-hidden="true">📉</span>
                New here? Start with why money loses value
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </Link>
            )}
            {started && nextLesson && (
              <p className="text-sm text-muted-foreground">
                Up next:{" "}
                <Link
                  to={continueTo}
                  className="font-semibold text-foreground underline-offset-2 hover:text-primary hover:underline"
                >
                  {nextLesson.title}
                </Link>
              </p>
            )}
          </div>
          <div className="relative">
            <img
              src="/hero.jpg"
              alt="BCH School — playful illustration of learning Bitcoin Cash"
              className="w-full rounded-2xl object-cover shadow-md"
              loading="eager"
            />
          </div>
        </div>
      </section>

      {/* Progress overview */}
      <section className="mb-10">
        <Card className="border-2">
          <CardContent className="grid gap-6 p-6 sm:grid-cols-3">
            <div className="space-y-2 sm:col-span-2">
              <div className="flex items-center justify-between">
                <h2 className="font-bold">Your progress</h2>
                <span className="text-sm font-semibold text-muted-foreground">
                  {completedCount}/{totalLessons} lessons · {percent}%
                </span>
              </div>
              <Progress value={percent} className="h-3" />
              <p className="text-sm text-muted-foreground">
                {allLessonsComplete && quizPassed
                  ? "You did it! Grab your certificate below. 🎓"
                  : allLessonsComplete
                    ? "All lessons done! Pass the final quiz to graduate. 🧠"
                    : currentStreak > 0 && !activeToday
                      ? `🔥 ${currentStreak}-day streak — finish a lesson today to keep it alive!`
                      : currentStreak > 1
                        ? `🔥 You're on a ${currentStreak}-day streak. Keep it going!`
                        : started
                          ? "Nice momentum — come back daily to build a streak!"
                          : "Complete lessons to earn XP and unlock badges."}
              </p>
              <div className="flex flex-wrap gap-2 pt-1">
                {allLessonsComplete && (
                  <Button asChild size="sm">
                    <Link to={quizPassed ? "/certificate" : "/quiz"}>
                      {quizPassed ? (
                        <>
                          <GraduationCap className="mr-1.5 h-4 w-4" /> View
                          certificate
                        </>
                      ) : (
                        <>
                          <BrainCircuit className="mr-1.5 h-4 w-4" /> Take final
                          quiz
                        </>
                      )}
                    </Link>
                  </Button>
                )}
                <ShareSiteButton />
                <ResetProgressButton />
              </div>
            </div>
            <div className="flex items-center justify-around gap-4 rounded-2xl bg-secondary/60 p-4">
              <Stat icon={<Zap className="h-5 w-5 fill-current" />} value={`${xp}`} label={`/ ${totalXp} XP`} />
              <div className="h-10 w-px bg-border" />
              <Stat icon={<Award className="h-5 w-5" />} value={`${earnedBadges.length}`} label="badges" />
              <div className="h-10 w-px bg-border" />
              <Stat
                icon={<Flame className="h-5 w-5 fill-current" />}
                value={`${currentStreak}`}
                label={currentStreak === 1 ? "day 🔥" : "days 🔥"}
              />
            </div>
          </CardContent>
        </Card>
      </section>

      {/* Modules */}
      <section className="mb-10">
        <h2 className="mb-4 text-2xl font-bold">Course modules</h2>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {MODULES.map((m) => (
            <ModuleCard
              key={m.id}
              module={m}
              done={moduleCompletion[m.id]?.done ?? 0}
              total={moduleCompletion[m.id]?.total ?? m.lessons.length}
            />
          ))}
        </div>
      </section>

      {/* Next steps — real world */}
      <section className="mb-4">
        <NextSteps />
      </section>
    </Layout>
  );
};

function Stat({
  icon,
  value,
  label,
}: {
  icon: React.ReactNode;
  value: string;
  label: string;
}) {
  return (
    <div className="text-center">
      <div className="flex items-center justify-center gap-1 text-primary">
        {icon}
        <span className="text-2xl font-extrabold tabular-nums text-foreground">
          {value}
        </span>
      </div>
      <p className="text-xs font-medium text-muted-foreground">{label}</p>
    </div>
  );
}

export default Index;
